import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import {
  Colors,
  ColorScheme,
  Fonts,
  getScrollbarStyle,
} from 'styles/constants';
import {
  BasePanel,
  BarChartIcon as BarIcon,
  ChevronRightCircleIcon,
  UserPinIcon as UserIcon,
} from 'styles/components';
export {
  ListIcon,
  CalendarIcon,
  SettingsIcon,
  CompanyIcon,
  UserRectangleIcon,
  AdminIcon,
  TruckIcon,
  WarehouseIcon,
  ReservedIntervalIcon,
  BookMarkIcon,
} from 'styles/components';

interface Recall {
  recall?: boolean;
}

interface IMenuItem {
  $active?: boolean;
}

export const MenuButton = styled.div<{ $active?: boolean; $expanded?: boolean }>`
  display: flex;
  flex-direction: column;
  color: currentColor;
  padding: 8px 32px;
  height: ${({ $expanded }) => ($expanded ? 'auto' : '95px')};
  min-height: 95px;
  width: 100%;
  box-sizing: border-box;
  border-left: 4px solid #ffffff00;
  transition: all 0.7s;
  cursor: pointer;

  &:hover {
    border-left: 4px solid ${ColorScheme.Primary};
    cursor: pointer;
  }
  ${({ $active }) =>
    $active &&
    css`
      border-left: 4px solid ${ColorScheme.Primary};
    `}
`;

export const Submenu = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 32px;
  width: calc(100% - 32px);
  box-sizing: border-box;
  gap: 4px;
  padding: 0 8px 12px 0;
`;

export const SubmenuItem = styled.div<IMenuItem>`
  display: flex;
  flex-direction: column;
  color: currentColor;
  min-height: 32px;
  width: 100%;
  box-sizing: border-box;
  padding: 4px 8px;
  transition: background-color 0.2s;

  & > div {
    align-items: flex-start;
    gap: 0 8px;
    margin-bottom: 0;
  }

  p {
    min-width: 0;
    margin: 0;
    overflow-wrap: anywhere;
    line-height: 1.25;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: transparent;
    `}
`;

export const SubmenuLink = styled(Link)`
  color: currentColor;
  text-decoration: none;
  display: block;
  min-width: 0;
  width: 100%;
  cursor: pointer;
  font-size: 14px;
`;

export const SubmenuAction = styled.button`
  color: currentColor;
  text-decoration: none;
  display: block;
  min-width: 0;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  text-align: left;
`;

export const MenuItem = styled(Link)<IMenuItem & { isSubmenu?: boolean }>`
  display: flex;
  flex-direction: column;
  color: currentColor;
  padding: 8px 32px;
  height: ${({ isSubmenu }) => (isSubmenu ? '75px' : '95px')};
  width: 240px;
  border-left: 4px solid #ffffff00;
  transition: all 0.7s;

  &:hover {
    border-left: 4px solid ${ColorScheme.Primary};
    cursor: pointer;
  }

  ${({ $active }) =>
    $active &&
    css`
      border-left: 4px solid ${ColorScheme.Primary};
    `}

  ${({ isSubmenu }) =>
    isSubmenu &&
    css`
      padding: 6px 24px;
      margin-left: 0;

      ${MenuItemHeader} {
        font-size: 14px;
        margin-bottom: 6px;
      }

      ${MenuItemBody} {
        font-size: 11px;
      }
    `}
`;

export const MenuItemAction = styled.button<IMenuItem>`
  display: flex;
  flex-direction: column;
  color: currentColor;
  text-align: left;
  font: inherit;
  padding: 8px 32px;
  height: 95px;
  width: 240px;
  border: 0;
  border-left: 4px solid #ffffff00;
  background: transparent;
  transition: all 0.7s;

  &:hover {
    border-left: 4px solid ${ColorScheme.Primary};
    cursor: pointer;
  }

  ${({ $active }) =>
    $active &&
    css`
      border-left: 4px solid ${ColorScheme.Primary};
    `}
`;

export const MenuItemHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: ${Fonts.GilroySemiBold};
  font-size: 15px;
  margin-bottom: 8px;
  gap: 0 9px;
  transition: all 0.3s;

  &:hover {
    color: ${ColorScheme.Primary};
  }
`;

export const MenuItemBody = styled.p`
  font-size: 12px;
  font-family: ${Fonts.OpenSans};
  color: ${ColorScheme.LightText};
`;

// Body

export const NavigationMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 8px;
  ${getScrollbarStyle()};
  ::-webkit-scrollbar-thumb {
    border-color: ${Colors.Gray70};
    background-color: ${Colors.Gray50};
  }
  ::-webkit-scrollbar-track {
    background-color: ${Colors.Gray70};
  }
`;

export const LeftPanel = styled(BasePanel)<Recall>`
  flex: 1 0 100%;
  width: ${({ recall }) => (recall ? '90px' : '240px')};
  transition: width 0.7s;
  height: calc(100vh - 48px);
  overflow: hidden;
  flex-direction: column;
  position: sticky;
  top: 24px;
  background: ${Colors.Gray70};
  color: ${Colors.White};

  ${({ recall }) =>
    recall &&
    css`
      img {
        width: 70px;
      }
      ${MenuItem},
      ${MenuItemAction} {
        height: 45px;
      }
      ${MenuButton} {
        height: 45px;
      }
      ${MenuItemHeader} {
        p {
          opacity: 0;
        }
      }
      ${MenuItemBody} {
        opacity: 0;
      }
      ${NavigationMenu} {
        overflow-y: hidden;
      }
      ${Submenu} {
        opacity: 0;
        display: none;
        overflow: hidden;
      }
    `};
`;

export const Container = styled.div`
  position: sticky;
  top: 24px;
  width: min-content;
`;

export const CollapseButton = styled.button<Recall>`
  background: ${Colors.Gray70};
  border-radius: 50%;
  padding: 1px;
  width: 26px;
  height: 26px;
  position: absolute;
  transition: transform 0.5s;
  transform: ${({ recall }) => (recall ? 'rotate(0deg)' : 'rotate(180deg)')};
  right: -12px;
  top: 90px;
  z-index: 1;
`;

export const ArrowIcon = styled(ChevronRightCircleIcon)`
  color: ${Colors.Gray50};
  transition: color 0.4s;
  :hover {
    color: ${Colors.Gray30};
  }
`;

export const UserPinIcon = styled(UserIcon)`
  margin-left: -3px;
`;

export const BarChartIcon = styled(BarIcon)`
  margin-left: -3px;
`;

// Header

export const Header = styled.header`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const Logo = styled.figure`
  margin: 52px 0;
`;

export const LogoImage = styled.img`
  width: 130px;
  transition: width 0.7s;
`;

// Footer

export const Footer = styled.div`
  text-align: center;
  font-size: 11px;
  padding: 12px 24px;
  margin-top: auto;
  white-space: nowrap;
  color: ${Colors.Gray50};
`;
